/**
 * Global cart-write mutex queue.
 *
 * Serializes any HTTP request that mutates the Shopify cart so that two
 * writes are never in flight at the same time. Reads (GET) bypass.
 *
 * Installed by patching window.fetch here in utils.js, which is the first
 * script tag in <head>. All other bundles (global.bundle.js,
 * index.bundle.js / cart-rewards) load after this file is parsed, so their
 * `fetch` calls go through the patched version.
 */
(function installCartWriteQueue() {
  if (window.__cartQueueInstalled) return;
  window.__cartQueueInstalled = true;

  var CART_WRITE_PATHS = ['/cart/change', '/cart/update', '/cart/add', '/cart/clear'];
  var SLOT_TIMEOUT_MS = 45000;
  var DEBUG = false;
  var START_TIME = performance.now();
  var seq = 0;

  function ts() {
    return '+' + (performance.now() - START_TIME).toFixed(1) + 'ms';
  }

  function dlog() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('%c[cart]%c ' + ts(), 'color:#9333ea;font-weight:bold', 'color:#9333ea');
    console.log.apply(console, args);
  }

  var state = {
    chain: Promise.resolve(),
    pending: 0,
  };

  function isCartWrite(input, init) {
    var method = ((init && init.method) ||
      (input && typeof input === 'object' && input.method) ||
      'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD') return false;
    var url = typeof input === 'string'
      ? input
      : (input && input.url) || '';
    for (var i = 0; i < CART_WRITE_PATHS.length; i++) {
      if (url.indexOf(CART_WRITE_PATHS[i]) !== -1) return true;
    }
    return false;
  }

  function setPending(pending) {
    var wasBusy = state.pending > 0;
    state.pending = pending;
    var isBusy = pending > 0;
    if (wasBusy !== isBusy) {
      dlog('queue ' + (isBusy ? 'BUSY' : 'IDLE') + ' pending=' + pending);
      document.dispatchEvent(new CustomEvent('shapes:cart:queue:state', {
        detail: { busy: isBusy, depth: pending, pending: pending },
      }));
      document.dispatchEvent(new CustomEvent(
        isBusy ? 'shapes:cart:lock' : 'shapes:cart:unlock'
      ));
    } else {
      dlog('queue pending=' + pending + ' (still ' + (isBusy ? 'busy' : 'idle') + ')');
    }
  }

  var originalFetch = window.fetch.bind(window);

  function enqueue(task, meta) {
    var slotId = ++seq;
    setPending(state.pending + 1);
    dlog('enqueue#' + slotId + ' pending=' + state.pending, meta || {});
    var run = function () {
      dlog('run#' + slotId + ' starting');
      var timeoutId;
      var timeoutPromise = new Promise(function (_, reject) {
        timeoutId = setTimeout(function () {
          dlog('SLOT TIMEOUT#' + slotId, meta || {});
          reject(new Error('cart_queue_slot_timeout'));
        }, SLOT_TIMEOUT_MS);
      });
      var taskStart = performance.now();
      return Promise.race([task(), timeoutPromise]).then(function (result) {
        dlog('run#' + slotId + ' resolved in ' + (performance.now() - taskStart).toFixed(0) + 'ms', { status: result && result.status });
        return result;
      }, function (err) {
        dlog('run#' + slotId + ' REJECTED in ' + (performance.now() - taskStart).toFixed(0) + 'ms', { error: err && err.message });
        throw err;
      }).finally(function () {
        clearTimeout(timeoutId);
        setPending(Math.max(0, state.pending - 1));
      });
    };

    var result = state.chain.then(run, run);
    state.chain = result.catch(function () { /* keep chain alive */ });
    return result;
  }

  window.fetch = function patchedFetch(input, init) {
    if (!isCartWrite(input, init)) {
      return originalFetch(input, init);
    }
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'POST').toUpperCase();
    var bodyPreview = init && init.body ? String(init.body).slice(0, 200) : null;
    return enqueue(function () {
      return originalFetch(input, init);
    }, { url: url, method: method, body: bodyPreview });
  };

  window.__cartQueue = {
    get busy() { return state.pending > 0; },
    get depth() { return state.pending; },
    get pending() { return state.pending; },
    run: function (taskFn) { return enqueue(taskFn); },
    setDebug: function (v) { DEBUG = !!v; },
  };

  dlog('installed; window.__cartQueue ready, window.__cartQueue.setDebug(true) to inspect');
})();

/**
 * Sidecart interaction guard.
 *
 * While any cart lock is active, the drawer gets an overlay class and common
 * user interaction events inside the drawer are cancelled in capture phase.
 * This protects quantity controls, remove links, upsells, rewards, notes, and
 * checkout controls from double actions on slow devices.
 */
(function installCartDrawerGuard() {
  if (window.__cartDrawerGuardInstalled) return;
  window.__cartDrawerGuardInstalled = true;

  var manualLocks = 0;
  var queueBusy = false;

  function isBusy() {
    return manualLocks > 0 || queueBusy || !!(window.__cartQueue && window.__cartQueue.busy);
  }

  function applyGuardState() {
    var busy = isBusy();
    var drawers = document.querySelectorAll('[data-cart-drawer]');
    drawers.forEach(function (drawer) {
      drawer.classList.toggle('cart-drawer--busy', busy);
      drawer.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        drawer.setAttribute('data-cart-busy', 'true');
      } else {
        drawer.removeAttribute('data-cart-busy');
      }
    });
  }

  function isActivationKey(event) {
    return event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight';
  }

  function blockDrawerInteraction(event) {
    if (!isBusy()) return;
    var target = event.target;
    var drawer = target && target.closest ? target.closest('[data-cart-drawer]') : null;
    if (!drawer || !drawer.classList.contains('cart-drawer--busy')) return;
    if (event.type === 'keydown' && !isActivationKey(event)) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  }

  document.addEventListener('shapes:cart:lock', function () {
    manualLocks += 1;
    applyGuardState();
  });

  document.addEventListener('shapes:cart:unlock', function () {
    manualLocks = Math.max(0, manualLocks - 1);
    applyGuardState();
  });

  document.addEventListener('shapes:cart:queue:state', function (event) {
    queueBusy = !!(event.detail && event.detail.busy);
    applyGuardState();
  });

  ['pointerdown', 'touchstart', 'click', 'submit', 'input', 'change'].forEach(function (eventName) {
    document.addEventListener(eventName, blockDrawerInteraction, true);
  });
  document.addEventListener('keydown', blockDrawerInteraction, true);

  ['DOMContentLoaded', 'theme:cart-drawer:opening', 'theme:cart-drawer:open', 'shopify:section:load'].forEach(function (eventName) {
    document.addEventListener(eventName, applyGuardState);
  });

  window.__cartDrawerGuard = {
    get busy() { return isBusy(); },
    apply: applyGuardState,
    reset: function () {
      manualLocks = 0;
      queueBusy = false;
      applyGuardState();
    },
  };

  applyGuardState();
})();

/**
 * `formatMoney` originally from Shopify’s `theme-scripts` package
 * https://github.com/Shopify/theme-scripts/blob/master/packages/theme-currency/currency.js#L20
 */

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} formatString - shop money_format setting
 * @return {String} value - formatted value
 */

function formatMoney(cents, formatString = theme.moneyFormat) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

window.switchDOMContentLoaded = false;

document.addEventListener('DOMContentLoaded', () => {
  window.switchDOMContentLoaded = true;
});

window.switchOnDOMContentLoaded = (cb) => {
  if (window.switchDOMContentLoaded) {
    cb();
    return;
  }

  document.addEventListener('DOMContentLoaded', () => {
    cb();
  });
};

window.requestIdleCallback =
  window.requestIdleCallback ||
  function (cb) {
    const start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1);
  };

window.cancelIdleCallback =
  window.cancelIdleCallback ||
  function (id) {
    clearTimeout(id);
  };

function throttle(callback, limit) {
  var waiting = false; // Initially, we're not waiting
  return function () {
    // We return a throttled function
    if (!waiting) {
      // If we're not waiting
      callback.apply(this, arguments); // Execute users function
      waiting = true; // Prevent future invocations
      setTimeout(function () {
        // After a period of time
        waiting = false; // And allow future invocations
      }, limit);
    }
  };
}

/**
 * Wrapper around Object.assign that deletes null or undefined
 * keys from the provided object, making them fall back to
 * values in the defaults object.
 *
 *
 * @param {Object} defaults - An object with defaults for settings/config
 * @param {Object} provided - Provided settings/config object
 * @returns {Object} - Merged object
 */

function objectWithDefaults(defaults, provided) {
  filterObjectByValues(provided, (value) => {
    return value === null || value === undefined;
  });

  return Object.assign(defaults, provided);
}

function wrap(el, tagName = 'div') {
  const wrapper = document.createElement(tagName);

  el.parentNode.insertBefore(wrapper, el);

  wrapper.appendChild(el);

  return wrapper;
}

function wrapAll(nodes, wrapper) {
  // Cache the current parent and previous sibling of the first node.
  var parent = nodes[0].parentNode;
  var previousSibling = nodes[0].previousSibling;

  // Place each node in wrapper.
  //  - If nodes is an array, we must increment the index we grab from
  //    after each loop.
  //  - If nodes is a NodeList, each node is automatically removed from
  //    the NodeList when it is removed from its parent with appendChild.
  for (var i = 0; nodes.length - i; wrapper.firstChild === nodes[0] && i++) {
    wrapper.appendChild(nodes[i]);
  }

  // Place the wrapper just after the cached previousSibling
  parent.insertBefore(wrapper, previousSibling.nextSibling);

  return wrapper;
}

function unwrap(wrapper) {
  // place childNodes in document fragment
  var docFrag = document.createDocumentFragment();
  while (wrapper.firstChild) {
    var child = wrapper.removeChild(wrapper.firstChild);
    docFrag.appendChild(child);
  }

  // replace wrapper with document fragment
  wrapper.parentNode.replaceChild(docFrag, wrapper);
}

function initTeleport(el) {
  if (!el) return;

  const teleportCandidates = el.querySelectorAll('[data-should-teleport]');

  if (teleportCandidates.length) {
    teleportCandidates.forEach((teleportCandidate) => {
      teleportCandidate.setAttribute(
        'x-teleport',
        teleportCandidate.dataset.shouldTeleport
      );
    });
  }
}

async function fetchSectionHTML(url, selector) {
  const res = await fetch(url);
  const fetchedSection = await res.text();
  const result = querySelectorInHTMLString(selector, fetchedSection);
  if (result === null) {
    return null;
  } else {
    return result.innerHTML;
  }
}

function fetchConfigDefaults(acceptHeader = 'application/json') {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;',
      Accept: acceptHeader,
    },
  };
}

function parseDOMFromString(htmlString) {
  window.___shapesDOMParser = window.___shapesDOMParser || new DOMParser();

  return window.___shapesDOMParser.parseFromString(htmlString, 'text/html');
}

function querySelectorInHTMLString(selector, htmlString) {
  return parseDOMFromString(htmlString).querySelector(selector);
}

/**
 * Light wrapper around fetch for making common
 * requests easier. Provides very basic caching.
 */

window.__fetchCache = window.__fetchCache || {};

const RESPONSE_TYPE_JSON = 0;
const RESPONSE_TYPE_TEXT = 1;

async function fetchAndCache(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = false,
  responseType
) {
  if (__fetchCache[url] && !forceFresh) {
    return __fetchCache[url];
  }

  const responseReader =
    responseType === RESPONSE_TYPE_TEXT
      ? Response.prototype.text
      : Response.prototype.json;

  const res = await fetch(url, options);
  const data = responseReader.call(res);

  if (cacheTimeout && cacheTimeout > 0) {
    __fetchCache[url] = data;

    setTimeout(() => {
      delete __fetchCache[url];
    }, cacheTimeout);
  }

  return data;
}

/**
 * fetchHTML and fetchJSON cache responses for 5 seconds
 * by default; if a fresh request is required, set
 * forceFresh to true or use the freshHTML and freshJSON
 * helper functions.
 */

async function fetchHTML(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = false
) {
  return fetchAndCache(
    url,
    options,
    cacheTimeout,
    forceFresh,
    RESPONSE_TYPE_TEXT
  );
}

function freshHTML(url, options) {
  return fetchHTML(url, options, 0, true);
}

async function fetchJSON(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = false
) {
  return fetchAndCache(
    url,
    options,
    cacheTimeout,
    forceFresh,
    RESPONSE_TYPE_JSON
  );
}

function freshJSON(url, options) {
  return fetchJSON(url, options, 0, true);
}

async function fetchHTMLFragment(url, selector, forceFresh = false) {
  const fetchedHTMLString = forceFresh
    ? await freshHTML(url)
    : await fetchHTML(url);
  const fragment = querySelectorInHTMLString(selector, fetchedHTMLString);

  return fragment ? fragment.innerHTML : '';
}

function mdBreakpointMQL() {
  return window.matchMedia('(min-width: 768px)');
}

function isMdBreakpoint() {
  return window.mdBreakpointMQL().matches;
}

function maxLgBreakpointMQL() {
  return window.matchMedia('(max-width: 989px)');
}

function isMaxLgBreakpoint() {
  return window.maxLgBreakpointMQL().matches;
}

function lgBreakpointMQL() {
  return window.matchMedia('(min-width: 990px)');
}

function isLgBreakpoint() {
  return window.lgBreakpointMQL().matches;
}

function motionSafeMQL() {
  return window.matchMedia('(prefers-reduced-motion)');
}

function isMotionSafe() {
  return !window.motionSafeMQL().matches;
}

function kebabCase(subject) {
  if ([' ', '_'].includes(subject)) return subject;
  return subject
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[_\s]/, '-')
    .toLowerCase();
}

function clearURLSearchParams(url) {
  for (const key of [...url.searchParams.keys()]) {
    url.searchParams.delete(key);
  }
}

/**
 * paramsInput can be a string, a sequence of pairs,
 * or a record, as per:
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#examples
 *
 * It can also be an instance of URLSearchParams.
 *
 */

function _getURLByModifyingParams(
  urlString,
  paramsInput,
  clear = false,
  append
) {
  const url = new URL(urlString, window.location.origin);

  if (clear) {
    clearURLSearchParams(url);
  }

  const params = new URLSearchParams(paramsInput);

  const setOrAppendParam = append
    ? URLSearchParams.prototype.append
    : URLSearchParams.prototype.set;

  for (const [key, value] of params) {
    setOrAppendParam.call(url.searchParams, key, value);
  }

  return url;
}

function getURLWithParams(url, paramsInput, clear = false) {
  return _getURLByModifyingParams(url, paramsInput, clear, false);
}

function currentURLWithParams(paramsInput, clear = false) {
  return getURLWithParams(window.location.href, paramsInput, clear);
}

function getURLAddingParams(url, paramsInput, clear = false) {
  return _getURLByModifyingParams(url, paramsInput, clear, true);
}

function currentURLAddingParams(paramsInput, clear = false) {
  return getURLAddingParams(window.location.href, paramsInput, clear);
}

function iFrameCommand(iFrameEl, commandString) {
  if (!iFrameEl || !commandString) return;

  iFrameEl.contentWindow.postMessage(
    JSON.stringify({
      event: 'command',
      func: commandString,
      args: '',
    }),
    '*'
  );
}

function iFrameMethod(iFrameEl, methodString) {
  if (!iFrameEl || !methodString) return;

  iFrameEl.contentWindow.postMessage(
    JSON.stringify({
      method: methodString,
    }),
    '*'
  );
}

let touchDevice = false;

window.setTouch = function () {
  touchDevice = true;
};

window.isTouch = function () {
  return touchDevice;
};

function getModalLabel(modalSlotName, slotEl) {
  const modal = Alpine.store('modals')[modalSlotName];

  if (modal && modal.open) {
    const labelSourceEl = Array.from(slotEl.children).filter((el) =>
      el.hasAttribute('data-modal-label')
    )[0];

    if (labelSourceEl) {
      return labelSourceEl.dataset.modalLabel;
    }
  }

  return false;
}

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function asyncTimeout(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getSectionId(el) {
  if (!el._closestSectionId) {
    el._closestSectionId = el
      .closest('.shopify-section')
      .getAttribute('id')
      .replace('shopify-section-', '');
  }

  return el._closestSectionId;
}

function daysInMs(days) {
  return days * 24 * 60 * 60 * 1000;
}

function msInDays(ms) {
  return ms / 1000 / 60 / 60 / 24;
}

function isInTheFuture(msSinceEpoch) {
  return msSinceEpoch > Date.now();
}

function setExpiringStorageItem(key, value, expiresIn) {
  localStorage.setItem(
    key,
    JSON.stringify({ value, expires: Date.now() + expiresIn })
  );
}

function getExpiringStorageItem(key) {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  let valueObject;

  try {
    valueObject = JSON.parse(value);
  } catch (e) {}

  if (valueObject && valueObject.expires) {
    if (isInTheFuture(valueObject.expires)) {
      return valueObject.value;
    } else {
      localStorage.removeItem(key);
      return null;
    }
  }

  return null;
}
